/**
 * Family Groups
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/family-groups
 * Backend: groups.routes.ts (GET /groups/mine). Shows groups; family = category "family".
 * Chores: family-groups.routes.ts (personal-scoped), per selected group.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ComingSoonPage from '../components/ComingSoonPage';
import { groupsApi, familyGroupsApi, type GroupChoreItem } from '../services/api';

interface Group {
  id: string;
  name: string;
  description?: string;
  category?: string;
  memberCount?: number;
  eventCount?: number;
  myRole?: string;
  channels?: Array<{ id: string; name: string }>;
}

const FREQUENCY_OPTIONS = ['once', 'daily', 'weekly', 'monthly'];

const FamilyGroups: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [chores, setChores] = useState<GroupChoreItem[]>([]);
  const [choresLoading, setChoresLoading] = useState(false);
  const [choresError, setChoresError] = useState<string | null>(null);
  const [busyChoreId, setBusyChoreId] = useState<string | null>(null);

  const [showAddChore, setShowAddChore] = useState(false);
  const [choreTitle, setChoreTitle] = useState('');
  const [choreFrequency, setChoreFrequency] = useState('once');
  const [choreDueDate, setChoreDueDate] = useState('');
  const [choreReward, setChoreReward] = useState('');
  const [addingChore, setAddingChore] = useState(false);

  const loadChores = React.useCallback(async (groupId: string) => {
    setChoresLoading(true);
    setChoresError(null);
    try {
      const res: any = await familyGroupsApi.listChores(groupId);
      if (res?.success && Array.isArray(res?.data)) {
        setChores(res.data);
      } else {
        setChoresError(res?.error || 'Failed to load chores');
      }
    } catch {
      setChoresError('Failed to load chores');
    } finally {
      setChoresLoading(false);
    }
  }, []);

  const toggleGroupChores = (groupId: string) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      setChores([]);
      setShowAddChore(false);
      return;
    }
    setExpandedGroupId(groupId);
    setShowAddChore(false);
    loadChores(groupId);
  };

  const handleAddChore = async () => {
    if (!expandedGroupId || !choreTitle.trim()) return;
    setAddingChore(true);
    setChoresError(null);
    try {
      const res: any = await familyGroupsApi.addChore(expandedGroupId, {
        title: choreTitle.trim(),
        frequency: choreFrequency,
        dueDate: choreDueDate || undefined,
        rewardAmount: choreReward ? Number(choreReward) : undefined,
      });
      if (res?.success) {
        setChoreTitle('');
        setChoreFrequency('once');
        setChoreDueDate('');
        setChoreReward('');
        setShowAddChore(false);
        loadChores(expandedGroupId);
      } else {
        setChoresError(res?.error || 'Failed to add chore');
      }
    } catch {
      setChoresError('Failed to add chore');
    } finally {
      setAddingChore(false);
    }
  };

  const handleCompleteChore = async (choreId: string) => {
    if (!expandedGroupId) return;
    setBusyChoreId(choreId);
    setChoresError(null);
    try {
      const res: any = await familyGroupsApi.completeChore(choreId);
      if (res?.success) loadChores(expandedGroupId);
      else setChoresError(res?.error || 'Failed to complete chore');
    } catch {
      setChoresError('Failed to complete chore');
    } finally {
      setBusyChoreId(null);
    }
  };

  const handleDeleteChore = async (choreId: string) => {
    if (!expandedGroupId) return;
    setBusyChoreId(choreId);
    setChoresError(null);
    try {
      const res: any = await familyGroupsApi.deleteChore(choreId);
      if (res?.success) loadChores(expandedGroupId);
      else setChoresError(res?.error || 'Failed to delete chore');
    } catch {
      setChoresError('Failed to delete chore');
    } finally {
      setBusyChoreId(null);
    }
  };

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res: any = await groupsApi.getMyGroups();
      if (res?.success && Array.isArray(res?.data)) {
        const family = res.data.filter((g: Group) =>
          g.category && String(g.category).toLowerCase() === 'family'
        );
        setGroups(family.length > 0 ? family : res.data);
      } else if (res?.error) {
        setError(res.error);
      }
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isFamilyOnly = groups.length > 0 && groups.every((g) => g.category?.toLowerCase() === 'family');
  const showAllGroups = groups.length > 0 && !isFamilyOnly;
  const totalMembers = groups.reduce((sum, g) => sum + (g.memberCount ?? 0), 0);

  if (loading) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading…
      </div>
    );
  }

  if (error || groups.length === 0) {
    return (
      <ComingSoonPage
        title="Family Groups"
        icon="👨‍👩‍👧‍👦"
        subtitle="Private groups for family sharing and coordination"
        description="Create family groups for calendars, shopping, and shared lists. Create a group in Groups and set category to Family to see it here."
        actionLabel="Go to Groups"
        actionHref="/groups"
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      />
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '26px' }}>👨‍👩‍👧‍👦 Family Groups</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '15px' }}>
              {isFamilyOnly ? 'Your family groups' : 'Your groups'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Link to="/groups" state={{ createFamily: true }} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '8px', color: 'white', textDecoration: 'none', fontWeight: 600, fontSize: '14px' }}>
              Create Family Group
            </Link>
            <Link to="/groups" style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', textDecoration: 'none', fontWeight: 500, fontSize: '14px' }}>
              All Groups
            </Link>
            <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', opacity: 0.9 }}>
          <span>{groups.length} {groups.length === 1 ? 'group' : 'groups'}</span>
          <span>👥 {totalMembers} members</span>
        </div>
      </div>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        {showAllGroups && (
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
            No groups with category &quot;Family&quot; yet. Showing all your groups. Create a group in <Link to="/groups" style={{ color: '#667eea' }}>Groups</Link> and set category to Family to list it here.
          </p>
        )}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {groups.map((g) => (
            <li
              key={g.id}
              style={{
                padding: '18px',
                borderBottom: '1px solid #eee',
                background: '#fafafa',
                borderRadius: '12px',
                marginBottom: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <span style={{ fontWeight: 600, color: '#333', fontSize: '17px' }}>{g.name}</span>
                  {g.category && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#667eea', background: '#667eea15', padding: '2px 8px', borderRadius: '6px' }}>{g.category}</span>}
                  {g.myRole && <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: '4px' }}>Your role: {g.myRole}</span>}
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#666' }}>
                  {g.memberCount != null && <span>👥 {g.memberCount}</span>}
                  {g.eventCount != null && g.eventCount > 0 && <span>📅 {g.eventCount}</span>}
                </div>
              </div>
              {g.description && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>{g.description}</p>}
              {g.channels && g.channels.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                  {g.channels.slice(0, 5).map((ch: { id: string; name: string }) => (
                    <span key={ch.id} style={{ background: '#e8e8e8', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', color: '#555' }}>#{ch.name}</span>
                  ))}
                  {g.channels.length > 5 && <span style={{ fontSize: '12px', color: '#999' }}>+{g.channels.length - 5} more</span>}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <Link to="/groups" state={{ selectGroupId: g.id }} style={{ fontSize: '14px', color: '#667eea', fontWeight: 600 }}>Open in Groups →</Link>
                <button
                  type="button"
                  onClick={() => toggleGroupChores(g.id)}
                  style={{
                    padding: '6px 12px',
                    background: expandedGroupId === g.id ? '#667eea' : '#667eea15',
                    border: 'none',
                    borderRadius: '8px',
                    color: expandedGroupId === g.id ? 'white' : '#667eea',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  ✅ Chores {expandedGroupId === g.id ? '▲' : '▼'}
                </button>
              </div>

              {expandedGroupId === g.id && (
                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <strong style={{ fontSize: '14px', color: '#333' }}>Chores</strong>
                    <button
                      type="button"
                      onClick={() => setShowAddChore((s) => !s)}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(76,175,80,0.9)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Add Chore
                    </button>
                  </div>

                  {showAddChore && (
                    <div style={{ background: 'white', borderRadius: '10px', padding: '12px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                      <input
                        type="text"
                        placeholder="Chore title (e.g. Take out trash)"
                        value={choreTitle}
                        onChange={(e) => setChoreTitle(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <select
                          value={choreFrequency}
                          onChange={(e) => setChoreFrequency(e.target.value)}
                          style={{ flex: '1 1 120px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                        >
                          {FREQUENCY_OPTIONS.map((f) => (
                            <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                          ))}
                        </select>
                        <input
                          type="date"
                          value={choreDueDate}
                          onChange={(e) => setChoreDueDate(e.target.value)}
                          style={{ flex: '1 1 120px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                        />
                        <input
                          type="number"
                          placeholder="Reward $ (optional)"
                          value={choreReward}
                          onChange={(e) => setChoreReward(e.target.value)}
                          style={{ flex: '1 1 120px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={handleAddChore}
                          disabled={addingChore || !choreTitle.trim()}
                          style={{ padding: '8px 14px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: addingChore ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                        >
                          {addingChore ? 'Adding…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowAddChore(false); setChoreTitle(''); setChoreFrequency('once'); setChoreDueDate(''); setChoreReward(''); }}
                          style={{ padding: '8px 14px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '13px' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {choresError && <p style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '10px' }}>{choresError}</p>}

                  {choresLoading ? (
                    <p style={{ fontSize: '13px', color: '#666' }}>Loading chores…</p>
                  ) : chores.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#888' }}>No chores yet for this group.</p>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {chores.map((chore) => (
                        <li
                          key={chore.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                            padding: '10px',
                            background: 'white',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: chore.isDone ? '#94a3b8' : '#333',
                              textDecoration: chore.isDone ? 'line-through' : 'none',
                            }}>
                              {chore.title}
                            </span>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                              <span style={{ fontSize: '11px', color: '#667eea', background: '#667eea15', padding: '2px 8px', borderRadius: '6px' }}>{chore.frequency}</span>
                              {chore.assignedToId && <span style={{ fontSize: '11px', color: '#888' }}>Assigned: {chore.assignedToId}</span>}
                              {chore.dueDate && <span style={{ fontSize: '11px', color: '#888' }}>Due {new Date(chore.dueDate).toLocaleDateString(undefined, { dateStyle: 'short' })}</span>}
                              {chore.rewardAmount != null && <span style={{ fontSize: '11px', color: '#16a34a' }}>${chore.rewardAmount}</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {!chore.isDone && (
                              <button
                                type="button"
                                onClick={() => handleCompleteChore(chore.id)}
                                disabled={busyChoreId === chore.id}
                                style={{ padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: busyChoreId === chore.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                              >
                                Mark done
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteChore(chore.id)}
                              disabled={busyChoreId === chore.id}
                              style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: busyChoreId === chore.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                            >
                              Delete
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default FamilyGroups;
